import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";

import { parseChecklistInput } from "@/domains/pm/utils/checklist";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { CHECKLIST_ITEM_MAX_LENGTH } from "@/stores/pm/repository";
import { isImeEnter } from "./enterToSave";
import { PanelSectionLabel } from "./PanelSectionLabel";

interface ChecklistDraftFieldProps {
  items: string[];
  onChange: (items: string[]) => void;
  /** "Remove checklist": drop the field and everything staged in it. */
  onRemove: () => void;
  /** Bumped on every "Add checklist" choice: focuses the composer again. */
  focusToken?: number;
}

/**
 * A new issue's checklist: items are staged here and written once the issue
 * exists. Drawn as the detail panel's Checklist band (IssueChecklistSection) so
 * a new issue reads like an existing one, and with the same input rules — Enter
 * adds and keeps focus, a multi-line paste adds one item per line, Escape or
 * leaving an empty checklist puts it away.
 */
export function ChecklistDraftField({
  items,
  onChange,
  onRemove,
  focusToken = 0,
}: ChecklistDraftFieldProps) {
  const { t } = useLanguage();
  const copy = t.pm.issues.checklist;
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // The field appears because "Add checklist" was just chosen. A frame later,
  // as the panel's composer does: the menu that chose it settles focus first.
  useEffect(() => {
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [focusToken]);

  /** Stage `texts`; false when refused (too long), so the typed text stays. */
  const add = (texts: string[]): boolean => {
    if (texts.length === 0) return true;
    if (texts.some((text) => text.length > CHECKLIST_ITEM_MAX_LENGTH)) {
      toast.error(copy.tooLong);
      return false;
    }
    onChange([...items, ...texts]);
    setDraft("");
    return true;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isImeEnter(e.nativeEvent)) {
      // Never submit the form from here: Enter adds an item.
      e.preventDefault();
      add(parseChecklistInput(draft));
    } else if (e.key === "Escape") {
      // Cancels the line, not the dialog or panel around it.
      e.stopPropagation();
      setDraft("");
      if (items.length === 0) onRemove();
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    if (!/\r?\n/.test(pasted.trim())) return;
    e.preventDefault();
    add(parseChecklistInput(`${draft}${pasted}`));
  };

  // A typed item not yet entered still counts when the issue is created:
  // pressing Create blurs this input first. Left with nothing in it, the
  // checklist goes away again, as the panel's does.
  const onBlur = () => {
    const texts = parseChecklistInput(draft);
    if (texts.length > 0) {
      add(texts);
      return;
    }
    if (items.length === 0) onRemove();
  };

  const total = items.length;

  return (
    <div className="py-2.5">
      <div className="flex items-center justify-between gap-3">
        <PanelSectionLabel>
          {copy.title}
          {total > 0 && <span> · {fill(copy.progress, { done: "0", total: String(total) })}</span>}
        </PanelSectionLabel>
        <button
          type="button"
          // Keeps the composer's blur from dismissing the band under the click.
          onMouseDown={(e) => e.preventDefault()}
          onClick={onRemove}
          className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          {copy.removeChecklist}
        </button>
      </div>
      {total > 0 && (
        <ul className="mt-1.5 space-y-px">
          {items.map((text, index) => (
            <li
              // Staged texts may repeat; position is their identity here.
              key={`${index}:${text}`}
              className="group flex items-start gap-1 rounded py-0.5 pr-1"
            >
              {/* Where the panel's drag handle sits, so the boxes line up. */}
              <span className="w-4 shrink-0" aria-hidden />
              <span
                aria-hidden
                className="mt-[3px] h-3.5 w-3.5 shrink-0 rounded-sm border border-primary"
              />
              <span className="ml-1.5 min-w-0 flex-1 whitespace-pre-wrap break-words text-sm leading-5">
                {text}
              </span>
              <button
                type="button"
                onClick={() => onChange(items.filter((_, i) => i !== index))}
                aria-label={copy.deleteItem}
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-1 flex items-center gap-2 pl-5">
        <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <input
          id="issue-checklist-draft"
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={onBlur}
          maxLength={CHECKLIST_ITEM_MAX_LENGTH}
          placeholder={copy.addPlaceholder}
          aria-label={copy.addPlaceholder}
          className="h-7 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
    </div>
  );
}
